'use client';

import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';
import { defaultLanguage } from '@stereoagency/constants';
import flush from 'just-flush';
import { Container, InfoCardGridSection, TagFilter } from '@stereoagency/ui';
import {
  filterOptionsFromDocuments,
  findInKeyArray,
  jobAsProps
} from '@/utils';
import { jobsQuery } from './jobsQuery';
import { useClientUrl } from '@/hooks';

type Props = {
  enableTaxonomyFilter?: boolean;
  jobs: (typeof jobsQuery)[1];
};

export default function JobListingSection({
  enableTaxonomyFilter,
  jobs
}: Props) {
  const url = useClientUrl();
  const { t, lang } = useTranslation();
  const [selected, setSelected] = useState<string[]>(['all']);

  const sortedJobs = jobs.sort((a, b) => {
    if (a.datetime && b.datetime) {
      return a.datetime > b.datetime ? -1 : 1;
    }
    return 0;
  });

  const filteredJobs = sortedJobs.filter((job) => {
    if (selected.length === 1 && selected[0] === 'all') return true;
    if (!job.taxonomies?.length) return false;
    return job.taxonomies.some(
      (tax) => tax.slug?.current && selected.includes(tax.slug.current)
    );
  });

  const filters = filterOptionsFromDocuments(sortedJobs, (doc) =>
    flush(
      doc.taxonomies?.map((tax) => {
        const name = tax.name
          ? findInKeyArray(tax.name, [lang, defaultLanguage.id])
          : null;
        if (!tax.slug?.current || !name?.value) return null;
        return { label: name.value, value: tax.slug.current };
      }) ?? []
    )
  );

  const handleSelectFilter = (slug: string) => {
    if (slug === 'all') {
      setSelected(['all']);
    } else {
      setSelected(selected.filter((v) => v !== 'all').concat([slug]));
    }
  };

  const filtersParam = url?.searchParams.get('filters');
  useEffect(() => {
    const filtersParamOptions = flush(
      (filtersParam?.split(',') ?? []).map((value) =>
        filters.find((entry) => entry.value === value)
      )
    );
    if (filtersParamOptions.length) {
      setSelected(filtersParamOptions.map((opt) => opt.value));
    }
  }, [filtersParam]);

  if (!jobs.length) {
    return null;
  }

  return (
    <>
      {enableTaxonomyFilter && (
        <Container name={JobListingSection.name}>
          <TagFilter
            value={selected}
            options={[{ label: t('global:all'), value: 'all' }, ...filters]}
            accentColor="--colors-orange-700"
            onChange={setSelected}
          />
        </Container>
      )}
      <Container name={JobListingSection.name}>
        <InfoCardGridSection
          cards={flush(
            filteredJobs.map((job) => jobAsProps(job, lang, handleSelectFilter))
          )}
        />
      </Container>
    </>
  );
}
